import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';

const SalesTransactions = ({ transactions = [], isLoading = false, error = '', channels = [], onUpdateDate, selectedMonth, setSelectedMonth, monthOptions = [], selectedChannel, setSelectedChannel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [dateValue, setDateValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getSalesChannelName = (salesChannelId) => {
    if (!salesChannelId) return 'Unknown';
    const channel = channels.find((c) => c.id === salesChannelId);
    return channel ? channel.name : 'Unknown';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const ms = timestamp._seconds ? timestamp._seconds * 1000 : new Date(timestamp).getTime();
    return new Date(ms).toLocaleDateString();
  };

  const toDateInputValue = (timestamp) => {
    if (!timestamp) return new Date().toISOString().split('T')[0];
    const ms = timestamp._seconds ? timestamp._seconds * 1000 : new Date(timestamp).getTime();
    return new Date(ms).toISOString().split('T')[0];
  };

  const calculateTotal = (qtySold, salePrice) => {
    return (qtySold * salePrice).toFixed(2);
  };

  const handleOpenDatePicker = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setEditingTransaction(transaction);
    setDateValue(toDateInputValue(transaction.saleRecordedDate));
  };

  const handleCloseDatePicker = () => {
    setAnchorEl(null);
    setEditingTransaction(null);
    setDateValue('');
    setIsSubmitting(false);
  };

  const handleSaveDateChange = async () => {
    if (!editingTransaction || !onUpdateDate) return;
    setIsSubmitting(true);
    try {
      const success = await onUpdateDate(editingTransaction.id, dateValue);
      if (success) {
        handleCloseDatePicker();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalLabelColSpan = isMobile ? 2 : 6;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationOnIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Sales Transactions ({transactions.length})
          </Typography>
        </Box>
        {monthOptions.length > 0 && (
          <TextField
            select
            size="small"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ minWidth: 180 }}
            aria-label="Select month"
          >
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        )}
        {channels.length > 0 && setSelectedChannel && (
          <TextField
            select
            size="small"
            value={selectedChannel || 'all'}
            onChange={(e) => setSelectedChannel(e.target.value)}
            sx={{ minWidth: 180 }}
            aria-label="Select sales channel"
          >
            <MenuItem value="all">All Channels</MenuItem>
            {channels.map((channel) => (
              <MenuItem key={channel.id} value={channel.id}>{channel.name}</MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} aria-live="polite" aria-atomic="true">
          {error}
        </Alert>
      )}

      {transactions.length === 0 ? (
        <Alert severity="info">
          {(selectedMonth && selectedMonth !== 'all') || (selectedChannel && selectedChannel !== 'all')
            ? `No sales transactions for the selected filters.`
            : 'No sales transactions recorded yet. Transactions will appear here when items are marked as sold.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="Sales transactions">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Item Description</TableCell>
                {!isMobile && <TableCell>Item #</TableCell>}
                {!isMobile && <TableCell>Sales Channel</TableCell>}
                {!isMobile && <TableCell align="right">Quantity Sold</TableCell>}
                {!isMobile && <TableCell align="right">Sale Price</TableCell>}
                <TableCell align="right">Total</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                  <TableCell>{formatDate(transaction.saleRecordedDate)}</TableCell>
                  <TableCell>{transaction.itemDescription}</TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {transaction.itemNumber || ''}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && <TableCell>{getSalesChannelName(transaction.salesChannelId)}</TableCell>}
                  {!isMobile && <TableCell align="right">{transaction.qtySold}</TableCell>}
                  {!isMobile && <TableCell align="right">${parseFloat(transaction.salePrice).toFixed(2)}</TableCell>}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>${calculateTotal(transaction.qtySold, transaction.salePrice)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Adjust Date">
                      <IconButton size="small" onClick={(e) => handleOpenDatePicker(e, transaction)} aria-label="Adjust Date">
                        <EditCalendarIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ backgroundColor: 'grey.100', borderTop: '2px solid #ddd' }}>
                <TableCell colSpan={totalLabelColSpan} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  Total Sales:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.dark' }}>
                  ${transactions.reduce((sum, transaction) => sum + parseFloat(calculateTotal(transaction.qtySold, transaction.salePrice)), 0).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseDatePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Adjust Sale Date
          </Typography>
          <TextField
            fullWidth
            label="Sale Date"
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleCloseDatePicker} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveDateChange}
              disabled={isSubmitting || !dateValue}
            >
              {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Save'}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default SalesTransactions;
